/**
 * Lilja Trustpilot carousel — native overflow + CSS scroll-snap.
 * Arrows and dots enhance scroll; swipe is the browser's own scrolling.
 */
(function () {
  if (customElements.get('lilja-tp-carousel')) return;

  class LiljaTpCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.lilja-tp__track');
      if (!this.track) return;

      this.scroller = this.track;
      this.cards = Array.prototype.slice.call(this.track.children);
      this.prevBtn = this.querySelector('.lilja-tp__nav--prev');
      this.nextBtn = this.querySelector('.lilja-tp__nav--next');
      this.dotsEl = this.querySelector('.lilja-tp__dots');
      if (!this.cards.length) return;

      this.desktopSlide = parseInt(this.getAttribute('data-desktop-slide'), 10) || 3;
      this.mobileSlide = parseInt(this.getAttribute('data-mobile-slide'), 10) || 1;
      this.desktopMin = parseInt(this.getAttribute('data-desktop-min'), 10) || 750;
      this.readMore = this.getAttribute('data-read-more') || 'Læs mere';
      this.readLess = this.getAttribute('data-read-less') || 'Læs mindre';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayMs = this.reduceMotion
        ? 0
        : (parseInt(this.getAttribute('data-autoplay'), 10) || 0) * 1000;

      this.index = 0;
      this.visible = 1;
      this.autoplayTimer = null;
      this.scrollRaf = 0;
      this.alive = true;
      this.programmatic = false;

      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);

      this.cards.forEach(function (card) {
        var body = card.querySelector('.lilja-tp__body');
        var btn = card.querySelector('.lilja-tp__more');
        if (!body || !btn) return;
        requestAnimationFrame(function () {
          if (body.scrollHeight - body.clientHeight > 2) btn.hidden = false;
        });
        btn.addEventListener('click', function () {
          var expanded = body.classList.toggle('is-expanded');
          btn.textContent = expanded ? this.readLess : this.readMore;
        }.bind(this));
      }, this);

      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);

      this.resizeObserver = new ResizeObserver(function () {
        this.syncLayout();
        this.buildDots();
        this.syncIndexFromScroll();
      }.bind(this));
      this.resizeObserver.observe(this.scroller);

      var start = this.init.bind(this);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(start);
      } else {
        requestAnimationFrame(function () {
          requestAnimationFrame(start);
        });
      }
    }

    disconnectedCallback() {
      this.alive = false;
      this.stopAutoplay();
      if (this.scroller) this.scroller.removeEventListener('scroll', this.onScroll);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    }

    getVisible() {
      return window.innerWidth < this.desktopMin ? this.mobileSlide : this.desktopSlide;
    }

    isCenterSnap() {
      return this.cards[0] && getComputedStyle(this.cards[0]).scrollSnapAlign === 'center';
    }

    maxIndex() {
      return Math.max(0, this.cards.length - this.visible);
    }

    slideOffset() {
      if (this.cards.length < 2) {
        return this.cards[0] ? this.cards[0].offsetWidth : 0;
      }
      return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
    }

    syncLayout() {
      this.visible = this.getVisible();
    }

    goTo(i) {
      this.index = Math.min(Math.max(i, 0), this.maxIndex());
      var card = this.cards[this.index];
      if (!card || !this.scroller) return;
      var left = card.offsetLeft;
      if (this.isCenterSnap()) {
        left = card.offsetLeft - (this.scroller.clientWidth - card.offsetWidth) / 2;
      }
      var max = this.scroller.scrollWidth - this.scroller.clientWidth;
      left = Math.min(Math.max(0, left), Math.max(0, max));
      this.programmatic = true;
      this.scroller.scrollTo({
        left: left,
        behavior: this.reduceMotion ? 'auto' : 'smooth'
      });
      this.updateUI();
    }

    syncIndexFromScroll() {
      if (this.isCenterSnap()) {
        var viewCenter = this.scroller.scrollLeft + this.scroller.clientWidth / 2;
        var nearest = 0;
        var min = Infinity;
        for (var i = 0; i < this.cards.length; i++) {
          var card = this.cards[i];
          var d = Math.abs(card.offsetLeft + card.offsetWidth / 2 - viewCenter);
          if (d < min) {
            min = d;
            nearest = i;
          }
        }
        this.index = Math.min(Math.max(nearest, 0), this.maxIndex());
        this.updateUI();
        return;
      }
      var offset = this.slideOffset();
      if (!offset) return;
      var next = Math.round(this.scroller.scrollLeft / offset);
      this.index = Math.min(Math.max(next, 0), this.maxIndex());
      this.updateUI();
    }

    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(function () {
        this.scrollRaf = 0;
        this.syncIndexFromScroll();
        this.programmatic = false;
      }.bind(this));
    }

    updateUI() {
      var single = this.maxIndex() <= 0;
      if (this.prevBtn) {
        this.prevBtn.hidden = single;
        this.prevBtn.disabled = this.index <= 0;
      }
      if (this.nextBtn) {
        this.nextBtn.hidden = single;
        this.nextBtn.disabled = this.index >= this.maxIndex();
      }
      if (!this.dotsEl) return;
      var dots = this.dotsEl.querySelectorAll('.lilja-tp__dot');
      for (var d = 0; d < dots.length; d++) {
        var on = d === this.index;
        dots[d].classList.toggle('is-active', on);
        if (on) dots[d].setAttribute('aria-current', 'true');
        else dots[d].removeAttribute('aria-current');
      }
    }

    buildDots() {
      if (!this.dotsEl) return;
      this.dotsEl.innerHTML = '';
      var pages = this.maxIndex() + 1;
      if (pages <= 1) {
        this.dotsEl.hidden = true;
        return;
      }
      this.dotsEl.hidden = false;
      for (var p = 0; p < pages; p++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'lilja-tp__dot';
        dot.setAttribute('aria-label', 'Gå til side ' + (p + 1));
        dot.addEventListener('click', function (idx) {
          this.goTo(idx);
          this.restartAutoplay();
        }.bind(this, p));
        this.dotsEl.appendChild(dot);
      }
      this.updateUI();
    }

    startAutoplay() {
      if (!this.autoplayMs || this.autoplayTimer) return;
      this.autoplayTimer = setInterval(function () {
        this.goTo(this.index >= this.maxIndex() ? 0 : this.index + 1);
      }.bind(this), this.autoplayMs);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    init() {
      if (!this.alive || !this.isConnected) return;
      this.syncLayout();
      this.buildDots();
      this.syncIndexFromScroll();
      this.startAutoplay();
    }

    onPrev() {
      this.goTo(this.index - 1);
      this.restartAutoplay();
    }

    onNext() {
      this.goTo(this.index + 1);
      this.restartAutoplay();
    }

    onMouseEnter() {
      this.stopAutoplay();
    }

    onMouseLeave() {
      this.startAutoplay();
    }
  }

  customElements.define('lilja-tp-carousel', LiljaTpCarousel);
})();
